import { PropsWithChildren } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import ComputerIcon from '@mui/icons-material/Computer';
import ExtensionIcon from '@mui/icons-material/Extension';
import MapIcon from '@mui/icons-material/MyLocation';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import CreateComponentIcon from '@mui/icons-material/AddCircleOutline';
import LogoFull from './MeltwaterLogoFull';
import LogoIcon from './MeltwaterLogoIcon';
import { NavLink } from 'react-router-dom';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearch } from '@backstage/plugin-search';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  useSidebarPinState,
} from '@backstage/core-components';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/People';
import GraphQLLogoIcon from './GraphQLLogo';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { getThemeTokens, shellMetrics } from '../../theme/tokens';

const SidebarLogo = () => {
  const { isOpen } = useSidebarOpenState();
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const horizontalInset = isOpen
    ? shellMetrics.railOpenInset
    : shellMetrics.railClosedInset;

  return (
    <Box
      sx={{
        alignItems: isOpen ? 'flex-start' : 'center',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.625,
        height: 'auto',
        mb: `${shellMetrics.railLogoBottom}px`,
        px: `${horizontalInset}px`,
        pt: `${shellMetrics.railLogoTop}px`,
        width: '100%',
      }}
    >
      <NavLink
        to="/"
        aria-label="Home"
        style={{
          display: 'block',
          textDecoration: 'none',
          width: '100%',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: isOpen ? 'flex-start' : 'center',
            minHeight: isOpen
              ? shellMetrics.railRowHeight
              : shellMetrics.railSearchHeight,
            width: '100%',
          }}
        >
          {isOpen ? <LogoFull /> : <LogoIcon />}
        </Box>
      </NavLink>
      {isOpen && (
        <Typography
          sx={{
            color: alpha(tokens.textMuted, 0.9),
            fontSize: theme.typography.caption.fontSize,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            width: '100%',
          }}
        >
          Developer Portal
        </Typography>
      )}
    </Box>
  );
};

const SidebarStateContainer = ({ children }: PropsWithChildren<{}>) => {
  const { isPinned } = useSidebarPinState();

  return (
    <Box data-sidebar-state={isPinned ? 'pinned' : 'unpinned'}>{children}</Box>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <SidebarStateContainer>
      <Sidebar
        sidebarOptions={{
          drawerWidthClosed: shellMetrics.railClosedWidth,
          drawerWidthOpen: shellMetrics.railOpenWidth,
        }}
      >
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearch />
        </SidebarGroup>

        <SidebarDivider />

        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="/" text="Home" />
          <SidebarItem icon={ComputerIcon} to="catalog" text="Catalog" />
          <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          <SidebarItem icon={GroupIcon} to="teams" text="Teams" />
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          <SidebarItem
            icon={CreateComponentIcon}
            to="create"
            text="Create..."
          />
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            <SidebarItem icon={MapIcon} to="tech-radar" text="Tech Radar" />
          </SidebarScrollWrapper>
          <SidebarDivider />
          <SidebarScrollWrapper>
            <SidebarItem
              icon={GraphQLLogoIcon}
              to="apollo-explorer"
              text="GraphQL Explorer"
            />
          </SidebarScrollWrapper>
        </SidebarGroup>

        <SidebarSpace />

        <SidebarDivider />

        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
    </SidebarStateContainer>
    {children}
  </SidebarPage>
);
